import type { SocialPostPreviewCardMetadata, SocialPostPreviewMedia } from '@wix/ai-scheduling-assistant-common-lib/entities';

const LABELS: SocialPostPreviewCardMetadata['labels'] = {
  badge: 'Preview',
  postsNow: 'Goes out as soon as you approve it',
  scheduled: 'Scheduled for',
  textOnly: 'Text only',
  seeMore: 'See more',
  seeLess: 'See less',
};

const unsplash = (id: string) => `https://images.unsplash.com/photo-${id}?w=900&q=80&auto=format&fit=crop`;

const PHOTOS: SocialPostPreviewMedia[] = [
  '1517836357463-d25dfeac3438',
  '1534438327276-14e5300c3a48',
  '1571019613454-1cb2f99b2d8b',
  '1518611012118-696072aa579a',
  '1544367567-0f2fcb009e0b',
  '1552674605-db6ffd4facb5',
].map((id) => ({ url: unsplash(id), kind: 'image' }));

const LANDSCAPE_VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/23056/23056-720.mp4', kind: 'video' };
const PORTRAIT_VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/40246/40246-720.mp4', kind: 'video' };
const MISSING_VIDEO: SocialPostPreviewMedia = { url: 'https://assets.mixkit.co/videos/0/feature-frenzy-2-missing.mp4', kind: 'video' };
const AVATAR = unsplash('1544005313-94ddf0286df2');

const tiktok = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  network: 'tiktok',
  networkLabel: 'TikTok',
  accountName: 'featurefrenzy2',
  accountLabel: 'featurefrenzy2@example.com',
  accountPictureUrl: AVATAR,
  media: [],
  mediaCount: 0,
  postType: 'post',
  decisionId: `tt-${id}`,
  taskId: `tt-task-${id}`,
  labels: LABELS,
  ...card,
});

const youtube = (id: string, card: Partial<SocialPostPreviewCardMetadata>): SocialPostPreviewCardMetadata => ({
  network: 'youtube',
  networkLabel: 'YouTube',
  accountName: 'Feature Frenzy 2',
  accountLabel: 'featurefrenzy2@example.com',
  accountPictureUrl: AVATAR,
  media: [LANDSCAPE_VIDEO],
  mediaCount: 1,
  postType: 'video',
  decisionId: `yt-${id}`,
  taskId: `yt-task-${id}`,
  labels: { ...LABELS, postType: 'Video' },
  ...card,
});

export const TIKTOK_YOUTUBE_SAMPLES: { id: string; title: string; card: SocialPostPreviewCardMetadata }[] = [
  {
    id: 'tiktok-video',
    title: 'TikTok: video',
    card: tiktok('video', {
      postType: 'video',
      caption: 'Morning mobility in 30 seconds. Save this for tomorrow! #mobility #fitness #morningroutine',
      media: [PORTRAIT_VIDEO],
      mediaCount: 1,
      labels: { ...LABELS, postType: 'Video' },
    }),
  },
  {
    id: 'tiktok-landscape',
    title: 'TikTok: landscape video, scheduled, no picture',
    card: tiktok('landscape', {
      postType: 'video',
      accountPictureUrl: undefined,
      caption: 'Our new studio is ready.',
      media: [LANDSCAPE_VIDEO],
      mediaCount: 1,
      scheduledAt: '2026-10-30T07:30:00.000Z',
      labels: { ...LABELS, postType: 'Video' },
    }),
  },
  {
    id: 'tiktok-photos',
    title: 'TikTok: photo carousel, four photos',
    card: tiktok('photos', {
      postType: 'carousel',
      caption: 'Four moves, zero equipment. Swipe for the full routine and try it tonight. Link in bio for the full class schedule.',
      media: PHOTOS.slice(0, 4),
      mediaCount: 4,
      labels: { ...LABELS, postType: 'Carousel' },
    }),
  },
  {
    id: 'tiktok-photo',
    title: 'TikTok: one photo, no account name',
    card: tiktok('photo', {
      accountName: undefined,
      accountPictureUrl: undefined,
      caption: 'Sunday stretch class is back.',
      media: PHOTOS.slice(4, 5),
      mediaCount: 1,
    }),
  },
  {
    id: 'tiktok-not-shown',
    title: 'TikTok: three photos of five, one broken',
    card: tiktok('not-shown', {
      postType: 'carousel',
      caption: 'Behind the scenes at the studio.',
      media: [PHOTOS[1], { url: unsplash('0000000000000-feature-frenzy-2-missing'), kind: 'image' }, PHOTOS[2]],
      mediaCount: 5,
      labels: { ...LABELS, postType: 'Carousel', mediaNotShown: 'Photos or videos not shown here: 2' },
    }),
  },
  {
    id: 'tiktok-broken-video',
    title: 'TikTok: video that fails to load',
    card: tiktok('broken-video', { postType: 'video', caption: 'Coming soon.', media: [MISSING_VIDEO], mediaCount: 1 }),
  },
  {
    id: 'tiktok-text',
    title: 'TikTok: text only (generic card)',
    card: tiktok('text', { caption: 'A post TikTok cannot carry without media.' }),
  },
  {
    id: 'youtube-video',
    title: 'YouTube: video with title and description',
    card: youtube('video', {
      caption:
        '5-minute desk stretches you can do between meetings\n\nStiff neck? Tight shoulders? Follow along with coach Dana for five easy stretches you can do right at your desk.\n\nNew classes every week. #stretching #deskworkout #wellness',
    }),
  },
  {
    id: 'youtube-scheduled',
    title: 'YouTube: title only, scheduled, no channel name',
    card: youtube('scheduled', {
      accountName: undefined,
      accountPictureUrl: undefined,
      caption: 'Studio tour: everything you need to know before your first class',
      scheduledAt: '2026-10-30T07:30:00.000Z',
    }),
  },
  {
    id: 'youtube-portrait',
    title: 'YouTube: portrait video in the player',
    card: youtube('portrait', {
      caption: 'Quick core finisher\n\nThree rounds, forty seconds each.',
      media: [PORTRAIT_VIDEO],
    }),
  },
  {
    id: 'youtube-short',
    title: 'YouTube: Short',
    card: youtube('short', {
      postType: 'short',
      caption: 'One-minute posture fix\n\nDo this every morning for a week and feel the difference. #shorts #posture',
      media: [PORTRAIT_VIDEO],
      labels: { ...LABELS, postType: 'Short' },
    }),
  },
  {
    id: 'youtube-short-landscape',
    title: 'YouTube: Short from a landscape video, title only',
    card: youtube('short-landscape', {
      postType: 'short',
      caption: 'Our new studio in 20 seconds',
      labels: { ...LABELS, postType: 'Short' },
    }),
  },
  {
    id: 'youtube-broken',
    title: 'YouTube: video that fails to load',
    card: youtube('broken', { caption: 'Weekly class schedule', media: [MISSING_VIDEO] }),
  },
  {
    id: 'youtube-photo',
    title: 'YouTube: a photo only (generic card)',
    card: youtube('photo', { caption: 'A community post with a picture', media: PHOTOS.slice(0, 1), postType: 'post', labels: LABELS }),
  },
];
